import { useState } from "react";
import { toast } from "sonner";
import { Check, ArrowUpRight, Loader2, Send } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { GOOGLE_FORM_URL } from "@/lib/assets";

const GUIDELINES = [
  "Must be 100% unsigned / self-owned masters",
  "Accepted Audio: 320kbps MP3 or WAV",
  "Eligible Genres: Hard Rock, Metal, Gothic, Atmospheric, Hardcore, Post-Rock",
];

const SUBJECTS = ["General Inquiry", "Booking", "Media/PR"];

export default function Submissions() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Inquiry sent — we'll be in touch soon.");
      setForm({ name: "", email: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-charcoal border border-slate/30 focus:border-crimson px-4 py-3 text-softwhite placeholder:text-slate/60 outline-none transition-colors font-body";

  return (
    <div data-testid="submissions-page" className="pt-32 md:pt-40 bg-charcoal">
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16 pb-24 md:pb-32">
        <Reveal>
          <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-4">Connect</p>
          <h1 className="font-display font-black text-5xl md:text-7xl text-softwhite tracking-tight">CONNECT &amp; SUBMIT</h1>
          <p className="mt-5 text-slate text-lg font-body max-w-2xl">
            Direct submission portal for KSSR Radio and Sorrow &amp; Serenity Music inquiries.
          </p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-8">
          {/* LEFT — KSSR Submissions */}
          <Reveal>
            <div className="border border-slate/20 bg-surface p-8 md:p-10 h-full flex flex-col" data-testid="kssr-submissions-col">
              <h2 className="font-display text-3xl text-softwhite">KSSR Radio Submissions</h2>
              <p className="mt-4 text-slate leading-relaxed font-body">
                We are actively recruiting unsigned bands for 24/7 radio rotation.
              </p>

              <ul className="mt-8 space-y-4">
                {GUIDELINES.map((g) => (
                  <li key={g} className="flex items-start gap-3 text-softwhite/90 font-body">
                    <Check size={18} className="text-crimson-bright shrink-0 mt-1" />
                    {g}
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-10">
                <div className="border border-crimson/40 bg-charcoal p-6 crimson-glow">
                  <p className="text-softwhite font-body">
                    Ready to submit? Click below to fill out our direct licensing form.
                  </p>
                  <a
                    href={GOOGLE_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="kssr-form-link"
                    className="mt-5 group inline-flex items-center gap-3 bg-crimson hover:bg-crimson-bright text-softwhite px-7 py-3.5 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
                  >
                    Open Submission Form
                    <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          {/* RIGHT — Contact form */}
          <Reveal delay={0.15}>
            <div className="border border-slate/20 bg-surface p-8 md:p-10 h-full" data-testid="contact-col">
              <h2 className="font-display text-3xl text-softwhite">General Label Contact</h2>
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="contact-form">
                <div>
                  <label className="font-label uppercase tracking-[0.2em] text-[11px] text-slate">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="contact-name-input"
                    className={`mt-2 ${inputCls}`}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="font-label uppercase tracking-[0.2em] text-[11px] text-slate">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="contact-email-input"
                    className={`mt-2 ${inputCls}`}
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <label className="font-label uppercase tracking-[0.2em] text-[11px] text-slate">Subject</label>
                  <Select value={form.subject} onValueChange={(v) => update("subject", v)}>
                    <SelectTrigger
                      data-testid="contact-subject-select"
                      className="mt-2 w-full bg-charcoal border-slate/30 text-softwhite rounded-none h-auto py-3 focus:ring-crimson"
                    >
                      <SelectValue placeholder="Select a subject" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-slate/30 text-softwhite">
                      {SUBJECTS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`subject-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="focus:bg-crimson/30">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="font-label uppercase tracking-[0.2em] text-[11px] text-slate">Message</label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    data-testid="contact-message-input"
                    className={`mt-2 ${inputCls} resize-none`}
                    placeholder="Tell us what's on your mind…"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-3 bg-crimson hover:bg-crimson-bright disabled:opacity-60 text-softwhite px-8 py-4 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <><Send size={17} /> Send Inquiry</>}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
